// Condensed PDP purchase options: Buy Once / Subscribe & Save mode selector,
// exclusive quantity panels and price status.
(function () {
  function escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value || '';
    return element.innerHTML;
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  // Money values come pre-formatted from Liquid's money filters (presentment currency).
  function buildPriceMarkup(root, label) {
    const data = label.dataset;
    const hasSale = Boolean(data.specialPrice && data.youSave);
    const currentPrice = hasSale ? data.specialPrice : data.regularPrice;
    const currentLabel = hasSale ? root.dataset.salePriceLabel : root.dataset.regularPriceLabel;
    const amount = escapeAttribute(data.regularPricePerItem);
    const currency = escapeAttribute(root.dataset.currencyCode);

    let markup = `
      <div class="product-info-price">
        <div class="price-box price-final_price" data-role="priceBox">
          <span class="special-price">
            <span class="price-container price-final_price">
              <span class="price-label">${escapeHtml(currentLabel)}</span>
              <span data-price-amount="${amount}" data-price-type="finalPrice" class="price-wrapper">
                <span class="price">${currentPrice}</span>
              </span>
              <meta itemprop="price" content="${amount}">
              <meta itemprop="priceCurrency" content="${currency}">
            </span>
          </span>
        </div>`;

    if (hasSale) {
      markup += `
        <span class="you-save">${data.youSave}</span>
        <span class="old-price hide-on-grid">
          <span class="price-container price-final_price">
            <span class="price-label">${escapeHtml(root.dataset.regularPriceLabel)}</span>
            <span data-price-amount="${amount}" data-price-type="oldPrice" class="price-wrapper">
              <span class="price">${data.regularPrice}</span>
            </span>
          </span>
        </span>`;
    }

    return `${markup}</div>`;
  }

  function initPurchaseOptions(root) {
    if (root.dataset.purchaseOptionsReady === 'true') return;
    root.dataset.purchaseOptionsReady = 'true';

    const form = root.closest('form');
    const variantInput = form ? form.querySelector('input[name="id"]') : null;
    const priceStatus = root.querySelector('[data-purchase-options-price]');
    const panels = Array.from(root.querySelectorAll('[data-purchase-mode-panel]'));
    const modeButtons = Array.from(root.querySelectorAll('[data-purchase-mode]'));
    const choiceButtons = Array.from(root.querySelectorAll('[data-purchase-choice]'));

    function renderPrice(choiceButton) {
      const priceNode = priceStatus ? priceStatus.querySelector('.price__regular') : null;
      if (!priceNode) return;
      priceNode.innerHTML = buildPriceMarkup(root, choiceButton);
    }

    // Every option is a toggle button (Tab-reachable); the hidden radios beside it
    // carry the `quantity` / `selling_plan` values the product form submits.
    function selectChoice(choiceButton) {
      const choice = choiceButton.closest('.purchase-options__choice');
      const panel = choiceButton.closest('[data-purchase-mode-panel]');
      const quantityInput = choice ? choice.querySelector('input[name="quantity"]') : null;
      if (!panel || !quantityInput || quantityInput.disabled) return;

      quantityInput.checked = true;
      const sellingPlanInput = choice.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput) sellingPlanInput.checked = true;

      for (const button of choiceButtons) {
        button.setAttribute('aria-pressed', String(button === choiceButton));
      }

      if (variantInput) {
        variantInput.value =
          panel.dataset.purchaseModePanel === 'subscription'
            ? root.dataset.subscriptionVariantId
            : root.dataset.singleVariantId;
      }

      renderPrice(choiceButton);
    }

    function activateMode(mode) {
      let activePanel = null;
      for (const panel of panels) {
        const isActive = panel.dataset.purchaseModePanel === mode;
        panel.hidden = !isActive;
        if (isActive) activePanel = panel;
      }
      for (const button of modeButtons) {
        button.setAttribute('aria-pressed', String(button.dataset.purchaseMode === mode));
      }
      if (!activePanel) return;

      const firstChoice = Array.from(activePanel.querySelectorAll('[data-purchase-choice]')).find((button) => {
        const quantityInput = button.closest('.purchase-options__choice').querySelector('input[name="quantity"]');
        return quantityInput && !quantityInput.disabled;
      });
      if (firstChoice) selectChoice(firstChoice);
    }

    for (const button of modeButtons) {
      button.addEventListener('click', () => activateMode(button.dataset.purchaseMode));
    }

    for (const button of choiceButtons) {
      button.addEventListener('click', () => selectChoice(button));
    }

    activateMode('single');
    initAddToCart(root, form);
  }

  // Mobile: once the shopper scrolls past the add-to-cart box, the button floats
  // at the bottom of the screen (.out-of-view) and brings them back to the options.
  function initAddToCart(root, form) {
    const box = root.querySelector('.box-tocart');
    const button = box ? box.querySelector('.action.tocart') : null;
    if (!button || !form) return;

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let isScrolledPast = false;

    function updateFloating() {
      const isFloating = mobileQuery.matches && isScrolledPast;
      button.classList.toggle('out-of-view', isFloating);
      document.body.classList.toggle('hide-gladly', isFloating);
    }

    new IntersectionObserver(([entry]) => {
      // Written as `0 >` because theme-check parses a bare less-than sign as an HTML tag.
      isScrolledPast = !entry.isIntersecting && 0 > entry.boundingClientRect.top;
      updateFloating();
    }).observe(box);
    mobileQuery.addEventListener('change', updateFloating);

    button.addEventListener('click', () => {
      if (button.classList.contains('out-of-view')) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        const selectedOption = root.querySelector('[data-purchase-mode][aria-pressed="true"], [data-purchase-choice][aria-pressed="true"]');
        if (selectedOption) selectedOption.focus({ preventScroll: true });
        return;
      }
      localStorage.setItem('criteoAddToCart', 'true');
      form.submit();
    });
  }

  function initAll(scope) {
    for (const root of scope.querySelectorAll('[data-purchase-options]')) {
      initPurchaseOptions(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
